import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { usersCollection } from '../schema';
import type { UserPreferences } from '../schema';

export interface GDPRConsentData {
  hasConsented: boolean;
  consentDate: Date | null;
  consentVersion: string;
  analytics: boolean;
  advertising: boolean;
  functional: boolean;
}

class ConsentService {
  /**
   * Save user's GDPR consent preferences to Firestore
   */
  async saveUserConsent(userId: string, consentData: GDPRConsentData): Promise<void> {
    try {
      if (!userId) {
        console.warn('Cannot save consent: No user ID provided');
        return;
      }

      const userRef = doc(usersCollection, userId);
      
      // Get current user data first
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        console.error('User document not found');
        return;
      }
      
      // Update only the gdprConsent field in preferences
      await updateDoc(userRef, {
        'preferences.gdprConsent': {
          hasConsented: consentData.hasConsented,
          consentDate: consentData.consentDate || new Date(),
          consentVersion: consentData.consentVersion,
          analytics: consentData.analytics,
          advertising: consentData.advertising,
          functional: consentData.functional
        }
      });
      
      console.log('GDPR consent preferences saved for user:', userId);
    } catch (error) {
      console.error('Error saving GDPR consent preferences:', error);
      throw error;
    }
  }

  /**
   * Get user's GDPR consent preferences from Firestore
   */
  async getUserConsent(userId: string): Promise<GDPRConsentData | null> {
    try {
      if (!userId) {
        console.warn('Cannot get consent: No user ID provided');
        return null;
      }

      const userRef = doc(usersCollection, userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        console.error('User document not found');
        return null;
      }
      
      const userData = userDoc.data();
      const preferences = userData.preferences as UserPreferences;
      
      if (!preferences || !preferences.gdprConsent) {
        return null;
      }
      
      // Convert Firestore timestamp to Date if needed
      let consentDate: Date | null = null;
      
      if (preferences.gdprConsent.consentDate instanceof Date) {
        consentDate = preferences.gdprConsent.consentDate;
      } else if (preferences.gdprConsent.consentDate && 
                typeof preferences.gdprConsent.consentDate === 'object' && 
                'toDate' in preferences.gdprConsent.consentDate) {
        // Handle Firestore Timestamp objects
        // Use type assertion to handle Firestore Timestamp
        const timestamp = preferences.gdprConsent.consentDate as { toDate(): Date };
        consentDate = timestamp.toDate();
      }
      
      return {
        hasConsented: preferences.gdprConsent.hasConsented || false,
        consentDate,
        consentVersion: preferences.gdprConsent.consentVersion || '1.0',
        analytics: preferences.gdprConsent.analytics || false,
        advertising: preferences.gdprConsent.advertising || false,
        functional: preferences.gdprConsent.functional || false
      };
    } catch (error) {
      console.error('Error getting GDPR consent preferences:', error);
      return null;
    }
  }

  /**
   * Update user's advertising consent preference
   */
  async updateAdConsent(userId: string, adConsent: boolean): Promise<void> {
    try {
      if (!userId) {
        console.warn('Cannot update ad consent: No user ID provided');
        return;
      }

      const userRef = doc(usersCollection, userId);
      
      await updateDoc(userRef, {
        'preferences.adConsent': adConsent,
        'preferences.gdprConsent.advertising': adConsent
      });
      
      console.log('Ad consent updated for user:', userId);
    } catch (error) {
      console.error('Error updating ad consent:', error);
      throw error;
    }
  }
}

export const consentService = new ConsentService();